import type { DocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { EditCompanyNameField } from '@/components/EditCompanyNameField';
import { EditDynamicSelectField } from '@/components/EditDynamicSelectField';
import { EditHrEmployeeSelectField } from '@/components/EditHrEmployeeSelectField';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

const CREATE_CONTACT = gql`
  mutation CreateContact($values: ContactInput!) {
    createContact(values: $values) {
      ok
      contact {
        id
        name
      }
    }
  }
`;

interface CreateContactFormData {
  companyName: string;
  companyNameId: null | string;
  email: string;
  firstName: string;
  jobTitle: string;
  lastName: string;
  mobile: string;
  owner: string;
  ownerId: null | string;
  phone: string;
  relationshipType: string;
}

const initialFormData: CreateContactFormData = {
  companyName: '',
  companyNameId: null,
  email: '',
  firstName: '',
  jobTitle: '',
  lastName: '',
  mobile: '',
  owner: '',
  ownerId: null,
  phone: '',
  relationshipType: ''
};

export interface CreateContactDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  refetchQuery?: { query: DocumentNode; variables?: Record<string, unknown> };
}

export function CreateContactDialog({
  isOpen,
  onOpenChange,
  refetchQuery
}: CreateContactDialogProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] =
    useState<CreateContactFormData>(initialFormData);

  const [createContact] = useMutation(CREATE_CONTACT, {
    refetchQueries: refetchQuery ? [refetchQuery] : []
  });

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fullName = [formData.firstName.trim(), formData.lastName.trim()]
      .filter(Boolean)
      .join(' ');

    if (!fullName) {
      toast.error('First name or last name is required');
      return;
    }

    setIsSaving(true);

    try {
      const values: Record<string, boolean | null | string> = {
        isCompany: false,
        name: fullName
      };

      if (formData.firstName.trim())
        values.firstName = formData.firstName.trim();
      if (formData.lastName.trim()) values.lastName = formData.lastName.trim();
      if (formData.email.trim()) values.email = formData.email.trim();
      if (formData.phone.trim()) values.phone = formData.phone.trim();
      if (formData.mobile.trim()) values.mobile = formData.mobile.trim();
      if (formData.jobTitle.trim()) values.function = formData.jobTitle.trim();
      if (formData.relationshipType)
        values.relationshipType = formData.relationshipType;
      if (formData.ownerId) values.ownerId = formData.ownerId;
      if (formData.companyNameId) values.companyNameId = formData.companyNameId;

      const response = await createContact({
        variables: { values }
      });

      const data = response.data as {
        createContact?: { contact?: { id: string }; ok?: boolean };
      };

      if (data.createContact?.ok && data.createContact.contact?.id) {
        toast.success('Contact created successfully');
        handleClose();
        void navigate(`/firm/crm-contacts/${data.createContact.contact.id}`);
      } else {
        toast.error('Failed to create contact');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (
    field: keyof CreateContactFormData,
    value: null | string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value: string): string => {
    const hasPlus = value.startsWith('+');
    const digits = value.replace(/\D/g, '');

    if (!digits) return hasPlus ? '+' : '';

    // US format: +1 (XXX) XXX-XXXX or (XXX) XXX-XXXX
    if (digits.length <= 11) {
      let formatted = '';
      let index = 0;

      // Handle country code
      if (hasPlus || digits.length === 11) {
        if (digits.length >= 1) {
          formatted = '+' + digits[0];
          index = 1;
        }
        if (digits.length > 1) {
          formatted += ' ';
        }
      }

      if (digits.length > index) {
        formatted += '(';
        formatted += digits.slice(index, index + 3);
        if (digits.length > index + 3) {
          formatted += ') ';
          formatted += digits.slice(index + 3, index + 6);
          if (digits.length > index + 6) {
            formatted += '-';
            formatted += digits.slice(index + 6, index + 10);
          }
        }
      }

      return formatted;
    }

    // For longer numbers, just add the plus and return digits
    return hasPlus ? '+' + digits : digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('mobile', formatted);
  };

  return (
    <Dialog
      onOpenChange={open => {
        if (!open) handleClose();
        else onOpenChange(open);
      }}
      open={isOpen}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Create New Contact</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            onSubmit={e => {
              void handleSubmit(e);
            }}
          >
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <Field className="gap-2">
                  <FieldLabel htmlFor="firstName">First Name</FieldLabel>
                  <Input
                    id="firstName"
                    onChange={e => {
                      updateField('firstName', e.target.value);
                    }}
                    placeholder="John"
                    value={formData.firstName}
                  />
                </Field>
                <Field className="gap-2">
                  <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
                  <Input
                    id="lastName"
                    onChange={e => {
                      updateField('lastName', e.target.value);
                    }}
                    placeholder="Doe"
                    value={formData.lastName}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field className="gap-2">
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    onChange={e => {
                      updateField('email', e.target.value);
                    }}
                    placeholder="john@example.com"
                    type="email"
                    value={formData.email}
                  />
                </Field>
                <Field className="gap-2">
                  <FieldLabel htmlFor="phone">Phone</FieldLabel>
                  <Input
                    id="phone"
                    onChange={handlePhoneChange}
                    placeholder="+1 (555) 000-0000"
                    type="tel"
                    value={formData.phone}
                  />
                </Field>
              </div>

              <Field className="gap-2">
                <FieldLabel htmlFor="mobile">Mobile</FieldLabel>
                <Input
                  id="mobile"
                  onChange={handleMobileChange}
                  placeholder="+1 (555) 000-0000"
                  type="tel"
                  value={formData.mobile}
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field className="gap-2">
                  <FieldLabel htmlFor="jobTitle">Job Title</FieldLabel>
                  <Input
                    id="jobTitle"
                    onChange={e => {
                      updateField('jobTitle', e.target.value);
                    }}
                    placeholder="Senior Manager"
                    value={formData.jobTitle}
                  />
                </Field>
                <EditCompanyNameField
                  label="Company Name"
                  onChange={(id, name) => {
                    setFormData(prev => ({
                      ...prev,
                      companyName: name,
                      companyNameId: id ?? null
                    }));
                  }}
                  value={
                    formData.companyNameId
                      ? {
                          id: formData.companyNameId,
                          name: formData.companyName
                        }
                      : null
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <EditDynamicSelectField
                  fieldName="relationship_type"
                  label="Relationship Type"
                  model="res.partner"
                  onChange={value => {
                    updateField('relationshipType', value);
                  }}
                  value={formData.relationshipType}
                />
                <EditHrEmployeeSelectField
                  label="Owner"
                  onChange={(id, name) => {
                    setFormData(prev => ({
                      ...prev,
                      owner: name,
                      ownerId: id || null
                    }));
                  }}
                  value={
                    formData.ownerId
                      ? { id: formData.ownerId, name: formData.owner }
                      : null
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                disabled={isSaving}
                onClick={handleClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  isSaving ||
                  (!formData.firstName.trim() && !formData.lastName.trim())
                }
                type="submit"
              >
                {isSaving ? 'Creating...' : 'Create Contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
